import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT, INTERESTS } from "@/data/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "border-ink/15 bg-white text-foreground placeholder:text-[#8B8577] focus-visible:ring-brand/50 rounded-none h-12";

export default function Contact() {
  usePageMeta(
    "Contact & Directions | SRK Bath and Tiles — Tigaon Road, Sector 86, Greater Faridabad",
    "Visit Shri Radhey Krishna Bath and Tiles on Tigaon Road, Sector 86, Greater Faridabad. Call or WhatsApp +91 98736 28088 for Jaquar, Kohler, Hindware & tile prices. Open daily 10 AM – 8 PM."
  );
  const [params] = useSearchParams();
  const brandParam = params.get("brand") || "";
  const defaultInterest = INTERESTS.find((i) => brandParam.toLowerCase().includes(i.split(" ")[0].toLowerCase())) || "";

  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: defaultInterest, message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interest) {
      toast.error("Please tell us what you're interested in.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, email: form.email || null });
      toast.success("Enquiry received. We will call you back within 24 hours.");
      setForm({ name: "", phone: "", email: "", interest: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pb-16 pt-36 sm:pt-48">
        <div className="pointer-events-none absolute -right-40 top-24 h-96 w-96 rounded-full bg-brand/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-brand"
          >
            Get In Touch
          </motion.p>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="contact-headline">
            {["Let's build your", "bathroom together."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.215, 0.61, 0.355, 1] }}
                >
                  {i === 1 ? <em className="text-brand-gradient not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Visit · Call · Write</Eyebrow>
            <ul className="mt-8 space-y-7">
              <li className="flex gap-4">
                <MapPin size={20} className="mt-1 shrink-0 text-brand" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8B8577]">Showroom</p>
                  <p className="mt-2 text-base leading-relaxed text-foreground" data-testid="contact-address">{CONTACT.address}</p>
                  <a
                    href={CONTACT.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-directions-link"
                    className="mt-2 inline-block font-mono text-xs uppercase tracking-[0.2em] text-brand underline-offset-4 hover:underline"
                  >
                    Get Directions →
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Phone size={20} className="mt-1 shrink-0 text-brand" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8B8577]">Phone &amp; WhatsApp · {CONTACT.owner}</p>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone" className="mt-2 block text-base text-foreground transition-colors hover:text-brand">
                    {CONTACT.phoneDisplay}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Mail size={20} className="mt-1 shrink-0 text-brand" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8B8577]">Email</p>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="mt-2 block break-all text-base text-foreground transition-colors hover:text-brand">
                    {CONTACT.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Clock size={20} className="mt-1 shrink-0 text-brand" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8B8577]">Hours</p>
                  <p className="mt-2 text-base text-foreground">{CONTACT.hours}</p>
                </div>
              </li>
            </ul>

            <div
              data-testid="contact-map-embed"
              className="img-spotlight mt-12 overflow-hidden border border-brand/25"
            >
              <iframe
                title="Shri Radhey Krishna Bath and Tiles — Google Map"
                src={CONTACT.mapEmbed}
                className="h-72 w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="img-spotlight relative border border-brand/25 bg-white p-8 sm:p-12"
            >
              <span className="absolute left-0 top-0 h-px w-full shimmer-line" />
              <Eyebrow>Enquiry Form</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl font-medium text-foreground sm:text-3xl">
                Tell us about your project.
              </h2>
              <p className="mt-3 text-sm text-[#5C6570]">We respond within 24 hours — usually much faster.</p>

              <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C6570]">
                    Full Name *
                  </label>
                  <Input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C6570]">
                    Phone Number *
                  </label>
                  <Input id="enquiry-phone" data-testid="enquiry-phone-input" required minLength={8} value={form.phone} onChange={set("phone")} placeholder="+91 …" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C6570]">
                    Email Address
                  </label>
                  <Input id="enquiry-email" type="email" data-testid="enquiry-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C6570]">
                    I'm Interested In *
                  </label>
                  <Select value={form.interest} onValueChange={set("interest")}>
                    <SelectTrigger data-testid="enquiry-interest-select" className={`${inputCls} font-normal`}>
                      <SelectValue placeholder="Choose a category" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-ink/15 bg-white">
                      {INTERESTS.map((i) => (
                        <SelectItem key={i} value={i} data-testid={`enquiry-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C6570]">
                    Message / Project Requirements *
                  </label>
                  <Textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-input"
                    required
                    minLength={5}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="e.g. Need 600×1200 Kajaria floor tiles for living room (~500 sq.ft) and Jaquar fittings for 2 bathrooms…"
                    className={`${inputCls} h-auto py-3`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="group mt-10 flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-brand-bright disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
